<template>
	<div>
		<s-crud
			title="Inventario Inicial y Kardex de Envases"
			:config="config"
			:filter="filter"
			height="calc(100vh - 200px)"
			:add="canManageInventory"
			:edit="canManageInventory"
			:remove="canManageInventory"
			searchInput
			@save="save($event)"
			ref="gridContainerInventory"
		>
			<!-- BOTÓN EXPORTAR EXCEL EN LA BARRA DE HERRAMIENTAS -->
			<template v-slot:options>
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							icon
							small
							v-bind="attrs"
							v-on="on"
							:loading="exportingExcel"
							@click="exportToExcel"
						>
							<v-icon style="font-size: 18px;" color="green lighten-1">mdi-microsoft-excel</v-icon>
						</v-btn>
					</template>
					<span>Exportar a Excel</span>
				</v-tooltip>
			</template>

			<!-- COLUMNA DE ACCIONES PERSONALIZADAS EN LA GRILLA -->
			<template v-slot:item.actions="{ item }">
				<v-btn
					x-small
					color="primary"
					class="text-capitalize font-weight-bold elevation-1 mr-1"
					@click.stop="openAdjustStockModal(item)"
				>
					<v-icon left x-small>mdi-package-up</v-icon>
					Ajustar Stock
				</v-btn>
				<v-btn
					x-small
					color="indigo darken-1"
					class="white--text text-capitalize font-weight-bold elevation-1 mr-1"
					@click.stop="openTransferModal(item)"
				>
					<v-icon left x-small>mdi-truck-fast</v-icon>
					Trasladar
				</v-btn>
				<v-btn
					x-small
					:color="hasHistory(item) ? 'teal darken-1' : 'grey lighten-2'"
					:outlined="!hasHistory(item)"
					:class="hasHistory(item) ? 'white--text font-weight-bold elevation-1 text-capitalize' : 'grey--text text--darken-1 font-weight-medium text-capitalize'"
					@click.stop="openHistoryModal(item)"
				>
					<v-icon left x-small :color="hasHistory(item) ? 'white' : 'grey darken-1'">mdi-history</v-icon>
					Historial
					<span v-if="hasHistory(item)" class="ml-1">({{ item.CinDetailCount }})</span>
				</v-btn>
			</template>

			<!-- FORMULARIO DE REGISTRO / EDICIÓN -->
			<template v-slot="props">
				<v-container fluid class="pa-2" v-if="props && props.item">
					<v-card flat class="cm-form-card cm-tone-teal mb-3">
						<div class="cm-section-header">
							 <div class="cm-section-icon">
								<v-icon size="16" color="#0d9488">mdi-warehouse</v-icon>
							</div>
							<div>
								<div class="cm-section-title">Registro de Inventario en Planta</div>
								<div class="cm-section-subtitle">Configuración de stock inicial y saldos por ubicación</div>
							</div>
						</div>
						<div class="cm-section-body">
							<v-row justify="start">
								<v-col cols="12" md="6" class="s-col-form pb-0">
									<v-autocomplete
										:items="containerMasterList"
										item-text="CmtDescription"
										item-value="CmtID"
										label="Envase / SKU *"
										v-model="props.item.CmtID"
										dense
										outlined
										hide-details="auto"
										class="s-custom-input"
										@focus="loadContainerMasterList"
									></v-autocomplete>
								</v-col>
								<v-col cols="12" md="6" class="s-col-form pb-0">
									<s-select-definition
										:def="1730"
										label="Ubicación / Planta *"
										v-model="props.item.CinLocationID"
										add
										clearable
										searchable
									></s-select-definition>
								</v-col>
								<v-col cols="12" md="2" class="s-col-form pb-0">
									<s-text
										label="Stock Inicial *"
										decimal
										v-model="props.item.CinInitialStock"
									></s-text>
								</v-col>
								<v-col cols="12" md="2" class="s-col-form pb-0">
									<s-text
										label="Stock Útil (Bueno)"
										decimal
										v-model="props.item.CinCurrentStock"
									></s-text>
								</v-col>
								<v-col cols="12" md="2" class="s-col-form pb-0">
									<s-text
										label="Mal Estado (En Planta)"
										decimal
										v-model="props.item.CinDefectiveStock"
									></s-text>
								</v-col>
								<v-col cols="12" md="3" class="s-col-form pb-0">
									<s-text
										label="En Campo (Deuda)"
										decimal
										v-model="props.item.CinBorrowedStock"
									></s-text>
								</v-col>
								<v-col cols="12" md="3" class="s-col-form pb-0">
									<s-text
										label="Pérdidas / Rotos"
										decimal
										v-model="props.item.CinLostStock"
									></s-text>
								</v-col>
							</v-row>
						</div>
					</v-card>
				</v-container>
			</template>
		</s-crud>

		<!-- MODAL FLOTANTE 1: REGISTRAR AJUSTE / RECARGA DE STOCK -->
		<v-dialog v-model="adjustDialog" max-width="560" persistent>
			<v-card class="rounded-lg">
				<v-card-title class="cm-modal-header pa-4 flex-nowrap">
					<v-icon color="#0f766e" class="mr-2">mdi-package-up</v-icon>
					<span class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
						Ajustar / Recargar Stock
					</span>
					<v-spacer></v-spacer>
					<v-btn icon small @click="adjustDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-card-text class="pa-4">
					<!-- Ficha resumen del saldo seleccionado -->
					<v-card flat class="pa-3 mb-3 cm-summary-card" outlined>
						<div class="d-flex justify-space-between align-center mb-1">
							<span class="caption text-uppercase font-weight-bold grey--text text--darken-2">Envase / SKU:</span>
							<span class="subtitle-2 font-weight-bold black--text">{{ selectedItem?.CmtDescription || selectedItem?.CmtCode || 'N/A' }}</span>
						</div>
						<div class="d-flex justify-space-between align-center mb-1">
							<span class="caption text-uppercase font-weight-bold grey--text text--darken-2">Ubicación / Planta:</span>
							<span class="subtitle-2 font-weight-bold black--text">{{ selectedItem?.CinLocationName || 'N/A' }}</span>
						</div>
						<div class="d-flex justify-space-between align-center">
							<span class="caption text-uppercase font-weight-bold grey--text text--darken-2">Stock Actual en Planta:</span>
							<span class="subtitle-2 font-weight-black teal--text text--darken-3">{{ selectedItem?.CinCurrentStock || 0 }} u.</span>
						</div>
					</v-card>

					<v-row dense>
						<v-col cols="12" class="s-col-form pb-2">
							<s-select-definition
								:def="1737"
								label="Tipo de Movimiento *"
								v-model="adjustModel.CidMovementType"
								clearable
								searchable
								add
								@updateData="onMovementDefinitionLoaded"
							></s-select-definition>
						</v-col>
						<v-col cols="12" md="6" class="s-col-form pb-2">
							<s-text
								label="Cantidad *"
								decimal
								v-model="adjustModel.CidQuantity"
							></s-text>
						</v-col>
						<v-col cols="12" md="6" class="s-col-form pb-2">
							<s-text
								label="N° Guía / Factura (Opcional)"
								v-model="adjustModel.CidDocument"
							></s-text>
						</v-col>
						<v-col cols="12" class="s-col-form pb-2">
							<s-text
								label="Motivo u Observación *"
								v-model="adjustModel.CidObservation"
							></s-text>
						</v-col>
					</v-row>

					<!-- Cálculo proyectado en vivo -->
					<div class="cm-projection-box pa-3 mt-2 rounded">
						<div class="caption font-weight-bold text-uppercase grey--text text--darken-3">Stock Resultante Proyectado:</div>
						<div class="subtitle-1 font-weight-black" :class="projectedStockClass">
							{{ projectedStock }} u.
						</div>
					</div>
				</v-card-text>

				<v-card-actions class="pa-4 pt-0">
					<v-spacer></v-spacer>
					<v-btn text color="grey darken-1" class="text-capitalize" @click="adjustDialog = false">
						Cancelar
					</v-btn>
					<v-btn color="primary" class="text-capitalize px-4 font-weight-bold" @click="confirmAdjust">
						<v-icon left small>mdi-content-save-check</v-icon>
						Confirmar Ajuste
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- MODAL FLOTANTE 2: TRASLADO DE ENVASES ENTRE PLANTAS (BUENO, MAL ESTADO O ROTOS) -->
		<v-dialog v-model="transferDialog" max-width="640" persistent>
			<v-card class="rounded-lg">
				<v-card-title class="cm-modal-header pa-4 flex-nowrap" style="background: #eef2ff; border-bottom: 1px solid #c7d2fe;">
					<v-icon color="#4338ca" class="mr-2">mdi-truck-fast</v-icon>
					<span class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
						Traslado de Envases entre Plantas / Ubicaciones
					</span>
					<v-spacer></v-spacer>
					<v-btn icon small @click="transferDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-card-text class="pa-4">
					<!-- Ficha resumen del origen -->
					<v-card flat class="pa-3 mb-3 cm-summary-card" outlined style="background: #f8fafc; border-color: #e2e8f0; border-radius: 8px;">
						<div class="d-flex justify-space-between align-center mb-1">
							<span class="caption text-uppercase font-weight-bold grey--text text--darken-2">Envase / SKU:</span>
							<span class="subtitle-2 font-weight-bold black--text">{{ selectedItem?.CmtDescription || 'N/A' }}</span>
						</div>
						<div class="d-flex justify-space-between align-center mb-2">
							<span class="caption text-uppercase font-weight-bold grey--text text--darken-2">Planta Origen:</span>
							<span class="subtitle-2 font-weight-bold indigo--text text--darken-3">{{ selectedItem?.CinLocationName || 'N/A' }}</span>
						</div>
						<div class="caption font-weight-bold text-uppercase grey--text text--darken-2 mb-1">Disponibilidad Actual en Origen:</div>
						<div class="d-flex justify-space-between flex-wrap gap-2">
							<v-chip small label outlined color="teal darken-2" class="font-weight-medium">
								Bueno: <strong>{{ selectedItem?.CinCurrentStock || 0 }} u.</strong>
							</v-chip>
							<v-chip small label outlined color="amber darken-4" class="font-weight-medium">
								Mal Estado: <strong>{{ selectedItem?.CinDefectiveStock || 0 }} u.</strong>
							</v-chip>
							<v-chip small label outlined color="red darken-2" class="font-weight-medium">
								Perdidos: <strong>{{ selectedItem?.CinLostStock || 0 }} u.</strong>
							</v-chip>
						</div>
					</v-card>

					<v-row dense>
						<!-- 1. Estado a trasladar -->
						<v-col cols="12" class="s-col-form pb-2">
							<v-select
								label="Estado / Condición a Trasladar *"
								:items="transferConditionOptions"
								v-model="transferModel.Condition"
								outlined
								dense
								hide-details="auto"
								class="s-custom-input"
							></v-select>
						</v-col>

						<!-- 2. Planta Destino -->
						<v-col cols="12" md="6" class="s-col-form pb-2">
							<s-select-definition
								:def="1730"
								label="Planta / Ubicación Destino *"
								v-model="transferModel.TargetLocationID"
								clearable
								searchable
							></s-select-definition>
						</v-col>

						<!-- 3. Cantidad a Trasladar -->
						<v-col cols="12" md="6" class="s-col-form pb-2">
							<s-text
								label="Cantidad a Trasladar *"
								decimal
								v-model="transferModel.Quantity"
							></s-text>
						</v-col>

						<!-- 4. N° Guía de Remisión / Documento -->
						<v-col cols="12" md="6" class="s-col-form pb-2">
							<s-text
								label="N° Guía / Documento"
								v-model="transferModel.Document"
							></s-text>
						</v-col>

						<!-- 5. Motivo / Observación -->
						<v-col cols="12" md="6" class="s-col-form pb-2">
							<s-text
								label="Motivo u Observación *"
								v-model="transferModel.Observation"
							></s-text>
						</v-col>
					</v-row>

					<!-- Indicador dinámico de la operación -->
					<v-alert
						dense
						text
						:type="isTransferValid ? 'info' : 'warning'"
						class="caption mt-3 mb-0"
					>
						<div v-if="!transferModel.TargetLocationID">
							Seleccione la <strong>Planta Destino</strong> para continuar.
						</div>
						<div v-else-if="transferModel.TargetLocationID == selectedItem?.CinLocationID">
							<v-icon small color="error">mdi-alert</v-icon>
							La <strong>Planta Destino</strong> debe ser distinta a la Planta de Origen.
						</div>
						<div v-else-if="transferQuantityExceeded">
							<v-icon small color="error">mdi-alert-circle</v-icon>
							La cantidad ({{ transferModel.Quantity }} u.) excede las <strong>{{ maxAvailableForCondition }} u.</strong> disponibles en esta condición en origen.
						</div>
						<div v-else-if="transferModel.Quantity > 0">
							<v-icon small color="info">mdi-check-circle</v-icon>
							Se trasladarán <strong>{{ transferModel.Quantity }} unidades</strong> en <strong>{{ selectedConditionLabel }}</strong> desde <em>{{ selectedItem?.CinLocationName }}</em>.
						</div>
						<div v-else>
							Disponible en origen para esta condición: <strong>{{ maxAvailableForCondition }} u.</strong>
						</div>
					</v-alert>
				</v-card-text>

				<v-card-actions class="pa-4 pt-0">
					<v-spacer></v-spacer>
					<v-btn text color="grey darken-1" class="text-capitalize" @click="transferDialog = false">
						Cancelar
					</v-btn>
					<v-btn
						color="indigo darken-1"
						class="white--text text-capitalize px-4 font-weight-bold"
						:disabled="!isTransferValid"
						:loading="loadingTransfer"
						@click="confirmTransfer"
					>
						<v-icon left small>mdi-swap-horizontal-bold</v-icon>
						Confirmar Traslado
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- MODAL FLOTANTE 3: HISTORIAL DE KARDEX / MOVIMIENTOS -->
		<v-dialog v-model="historyDialog" max-width="920" scrollable>
			<v-card class="rounded-lg">
				<v-card-title class="cm-modal-header pa-4 flex-nowrap">
					<v-icon color="#0f766e" class="mr-2">mdi-history</v-icon>
					<div>
						<div class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
							Historial de Movimientos de Inventario (Kardex)
						</div>
						<div class="text-caption grey--text text--darken-2">
							{{ selectedItem?.CmtDescription }} — {{ selectedItem?.CinLocationName }}
						</div>
					</div>
					<v-spacer></v-spacer>
					<v-btn icon small @click="historyDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>

				<v-card-text class="pa-4">
					<v-data-table
						:headers="historyHeaders"
						:items="historyList"
						:loading="loadingHistory"
						loading-text="Cargando historial de transacciones..."
						no-data-text="No se registran movimientos de ajuste para este inventario."
						dense
						class="elevation-0 cm-history-table"
						:items-per-page="10"
					>
						<template v-slot:item.SecCreate="{ item }">
							<span>{{ formatDateTime(item.SecCreate) }}</span>
						</template>

						<template v-slot:item.CidMovementType="{ item }">
							<v-chip
								x-small
								label
								:color="getMovementColor(item)"
								class="white--text font-weight-bold"
							>
								{{ item.CidMovementTypeName || ('Tipo #' + item.CidMovementType) }}
							</v-chip>
						</template>

						<template v-slot:item.CidQuantity="{ item }">
							<span :class="getQuantityClass(item)" class="font-weight-bold">
								{{ formatQuantity(item) }}
							</span>
						</template>
					</v-data-table>
				</v-card-text>

				<v-card-actions class="pa-4 pt-0">
					<v-spacer></v-spacer>
					<v-btn color="primary" class="text-capitalize px-4" @click="historyDialog = false">
						Cerrar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import SCrud from "@/components/Utils/SCrud.vue";
import SText from "@/components/Utils/SText.vue";
import SSelect from "@/components/Utils/SSelect.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import _sContainerInventoryService from "@/services/Logistics/ContainerInventoryService";
import _sContainerMasterService from "@/services/Logistics/ContainerMasterService";
import _sDefinition from "@/services/General/DefinitionService";
import moment from "moment";
import { exportVuetifyToExcel } from "@/utils/excelExport";

export default {
	name: "ContainerInventory",
	components: {
		SCrud,
		SText,
		SSelect,
		SSelectDefinition,
	},
	data() {
		return {
			filter: {},
			exportingExcel: false,
			containerMasterList: [],
			adjustDialog: false,
			transferDialog: false,
			loadingTransfer: false,
			transferModel: {
				Condition: "DEFECTIVE",
				TargetLocationID: null,
				Quantity: "",
				Document: "",
				Observation: "",
			},
			transferConditionOptions: [
				{ text: "Mal Estado (A lavar / reparar)", value: "DEFECTIVE" },
				{ text: "Perdidos (Bajas)", value: "LOST" },
				{ text: "Buen Estado (Útil / Operativo)", value: "GOOD" },
			],
			historyDialog: false,
			loadingHistory: false,
			selectedItem: null,
			historyList: [],
			adjustModel: {
				CinID: null,
				CidMovementType: 1,
				CidQuantity: "",
				CidDocument: "",
				CidObservation: "",
			},
			movementTypeList: [],
			historyHeaders: [
				{ text: "Fecha Registro", value: "SecCreate", width: "140" },
				{ text: "Tipo de Movimiento", value: "CidMovementType", width: "210" },
				{ text: "Cantidad", value: "CidQuantity", width: "110", align: "right" },
				{ text: "Documento / Guía", value: "CidDocument", width: "140" },
				{ text: "Motivo / Observación", value: "CidObservation" },
				{ text: "Registrado Por", value: "UsrCreateName", width: "130" },
			],
			config: {
				model: {
					CinID: "ID",
					CmtID: null,
					CinLocationID: null,
					CinInitialStock: "",
					CinCurrentStock: "",
					CinDefectiveStock: "",
					CinBorrowedStock: "",
					CinLostStock: "",
				},
				service: _sContainerInventoryService,
				headers: [
					{ text: "Acciones", value: "actions", sortable: false, width: "280" },
					{ text: "Código SKU", value: "CmtCode", width: "120" },
					{ text: "Descripción Envase", value: "CmtDescription", width: "220" },
					{ text: "Ubicación / Planta", value: "CinLocationName", width: "160" },
					{ text: "Stock Inicial", value: "CinInitialStock", width: "120" },
					{ text: "Stock Útil (Bueno)", value: "CinCurrentStock", width: "130" },
					{ text: "Mal Estado", value: "CinDefectiveStock", width: "120" },
					{ text: "En Campo (Deuda)", value: "CinBorrowedStock", width: "140" },
					{ text: "Perdidos", value: "CinLostStock", width: "130" },
				],
			},
		};
	},
	computed: {
		projectedStock() {
			const current = parseFloat(this.selectedItem?.CinCurrentStock) || 0;
			const qty = parseFloat(this.adjustModel.CidQuantity) || 0;
			const val = this.adjustModel.CidMovementType;
			if (!val) return current.toFixed(2);

			const found = this.movementTypeList.find((x) => x.DedValue == val);
			const helper = (found?.DedHelper || "").toUpperCase();
			const abbr = found?.DedAbbreviation || "";

			if (helper === "IN" || abbr === "+" || val == 1 || val == 2) {
				return (current + qty).toFixed(2);
			} else if (helper === "OUT" || helper === "LOST" || abbr === "-" || val == 3 || val == 4) {
				return Math.max(0, current - qty).toFixed(2);
			}
			return current.toFixed(2);
		},
		projectedStockClass() {
			const current = parseFloat(this.selectedItem?.CinCurrentStock) || 0;
			const proj = parseFloat(this.projectedStock) || 0;
			if (proj > current) return "teal--text text--darken-3";
			if (proj < current) return "orange--text text--darken-3";
			return "grey--text text--darken-3";
		},
		maxAvailableForCondition() {
			if (!this.selectedItem) return 0;
			const cond = this.transferModel?.Condition || "DEFECTIVE";
			if (cond === "DEFECTIVE") return parseFloat(this.selectedItem.CinDefectiveStock) || 0;
			if (cond === "LOST") return parseFloat(this.selectedItem.CinLostStock) || 0;
			if (cond === "GOOD") return parseFloat(this.selectedItem.CinCurrentStock) || 0;
			return 0;
		},
		transferQuantityExceeded() {
			const qty = parseFloat(this.transferModel?.Quantity) || 0;
			return qty > this.maxAvailableForCondition;
		},
		isTransferValid() {
			const target = this.transferModel?.TargetLocationID;
			const origin = this.selectedItem?.CinLocationID;
			const qty = parseFloat(this.transferModel?.Quantity) || 0;
			if (!target || target == origin) return false;
			if (qty <= 0) return false;
			if (this.transferQuantityExceeded) return false;
			return true;
		},
		selectedConditionLabel() {
			const cond = this.transferModel?.Condition || "DEFECTIVE";
			if (cond === "DEFECTIVE") return "Mal Estado (Lavado / Reparación)";
			if (cond === "LOST") return "Perdidos";
			if (cond === "GOOD") return "Buen Estado";
			return cond;
		},
		canManageInventory() {
			return !!(this.$fun.isAdmin() || (this.$fun.getSecurity() && this.$fun.getSecurity().IsLevelAdmin));
		},
	},
	mounted() {
		this.loadContainerMasterList();
		this.loadMovementTypes();
	},
	methods: {
		loadContainerMasterList() {
			_sContainerMasterService
				.list()
				.then((response) => {
					if (response.status === 200) {
						this.containerMasterList = response.data || [];
					}
				})
				.catch((error) => {
					console.error("Error al cargar la lista del maestro de envases:", error);
				});
		},
		loadMovementTypes() {
			_sDefinition
				.definitiongroup(1737, 0, this.$fun.getUserID())
				.then((response) => {
					if (response.status === 200) {
						const list = response.data || [];
						this.movementTypeList = list.filter((x) => parseInt(x.DedValue) <= 4);
					}
				})
				.catch((err) => {
					console.warn("Error al cargar tipos de movimiento de definición 1737:", err);
				});
		},
		onMovementDefinitionLoaded(items) {
			if (Array.isArray(items) && items.length > 0) {
				this.movementTypeList = items.filter((x) => parseInt(x.DedValue) <= 4);
			}
		},
		async save(item) {
			if (!item) return;
			if (!item.CmtID || !item.CinLocationID) {
				this.$fun.alert("Por favor seleccione el Envase y la Ubicación/Planta", "warning");
				return;
			}

			// Validación preventiva anti-duplicados para nuevos registros
			const isNew = !item.CinID || item.CinID === "ID" || item.CinID === 0;
			if (isNew) {
				try {
					const existing = await _sContainerInventoryService.getStock(
						item.CmtID,
						item.CinLocationID,
						this.$fun.getUserID()
					);
					if (existing && existing.status === 200 && existing.data && existing.data.CinID > 0) {
						this.$fun.alert(
							`El envase seleccionado ya cuenta con un saldo de inventario en esta planta (Stock Actual: ${existing.data.CinCurrentStock || 0} u.). Para recargar stock o registrar ingresos, utilice el botón "Ajustar Stock" en la fila del registro.`,
							"warning"
						);
						return;
					}
				} catch (err) {
					console.warn("Error al verificar existencia de saldo:", err);
				}
			}

			if (item.CinInitialStock === undefined || item.CinInitialStock === null) {
				item.CinInitialStock = 0;
			}

			item.UsrCreateID = this.$fun.getUserID();
			item.UsrUpdateID = this.$fun.getUserID();
			item.SecStatus = 1;

			item.save();
		},
		openAdjustStockModal(item) {
			this.selectedItem = Object.assign({}, item);
			this.adjustModel = {
				CinID: item.CinID,
				CidMovementType: 1,
				CidQuantity: "",
				CidDocument: "",
				CidObservation: "",
			};
			if (this.movementTypeList.length === 0) {
				this.loadMovementTypes();
			}
			this.adjustDialog = true;
		},
		openTransferModal(item) {
			this.selectedItem = Object.assign({}, item);
			// Por defecto sugerir la condición que tenga stock (prioridad a Mal Estado según requerimiento)
			let initialCondition = "DEFECTIVE";
			if ((parseFloat(item.CinDefectiveStock) || 0) > 0) {
				initialCondition = "DEFECTIVE";
			} else if ((parseFloat(item.CinLostStock) || 0) > 0) {
				initialCondition = "LOST";
			} else if ((parseFloat(item.CinCurrentStock) || 0) > 0) {
				initialCondition = "GOOD";
			}

			this.transferModel = {
				Condition: initialCondition,
				TargetLocationID: null,
				Quantity: "",
				Document: "",
				Observation: "",
			};
			this.transferDialog = true;
		},
		async confirmTransfer() {
			if (!this.isTransferValid) {
				this.$fun.alert("Por favor complete los datos de traslado correctamente", "warning");
				return;
			}

			const qty = parseFloat(this.transferModel.Quantity) || 0;
			const cond = this.transferModel.Condition;
			const condLabel = this.selectedConditionLabel;

			const confirm = await this.$fun.alert(
				`¿Confirmar el traslado de ${qty} unidades en [${condLabel}] hacia la planta de destino seleccionada?`,
				"question"
			);
			if (!confirm || (!confirm.value && !confirm.isConfirmed)) return;

			this.loadingTransfer = true;
			try {
				const userID = this.$fun.getUserID();
				const cmtID = this.selectedItem.CmtID;
				const targetLocationID = this.transferModel.TargetLocationID;

				// 1. Obtener o preparar el registro de stock destino
				let targetStock = null;
				try {
					const resTarget = await _sContainerInventoryService.getStock(cmtID, targetLocationID, userID);
					if (resTarget && resTarget.status === 200 && resTarget.data && resTarget.data.CinID > 0) {
						targetStock = resTarget.data;
					}
				} catch (errStock) {
					console.warn("Target stock not found, will create on save:", errStock);
				}

				// 2. Descontar en origen
				const originPayload = Object.assign({}, this.selectedItem);
				if (cond === "DEFECTIVE") {
					originPayload.CinDefectiveStock = Math.max(0, (parseFloat(originPayload.CinDefectiveStock) || 0) - qty);
				} else if (cond === "LOST") {
					originPayload.CinLostStock = Math.max(0, (parseFloat(originPayload.CinLostStock) || 0) - qty);
				} else if (cond === "GOOD") {
					originPayload.CinCurrentStock = Math.max(0, (parseFloat(originPayload.CinCurrentStock) || 0) - qty);
				}
				originPayload.UsrUpdateID = userID;

				await _sContainerInventoryService.save(originPayload, userID);

				// 3. Incrementar en destino
				let targetCinID = targetStock ? targetStock.CinID : 0;
				if (targetStock) {
					const targetPayload = Object.assign({}, targetStock);
					if (cond === "DEFECTIVE") {
						targetPayload.CinDefectiveStock = (parseFloat(targetPayload.CinDefectiveStock) || 0) + qty;
					} else if (cond === "LOST") {
						targetPayload.CinLostStock = (parseFloat(targetPayload.CinLostStock) || 0) + qty;
					} else if (cond === "GOOD") {
						targetPayload.CinCurrentStock = (parseFloat(targetPayload.CinCurrentStock) || 0) + qty;
					}
					targetPayload.UsrUpdateID = userID;
					await _sContainerInventoryService.save(targetPayload, userID);
				} else {
					// Crear nuevo registro para la planta destino
					const newTargetPayload = {
						CinID: 0,
						CmtID: cmtID,
						CinLocationID: targetLocationID,
						CinInitialStock: 0,
						CinCurrentStock: cond === "GOOD" ? qty : 0,
						CinDefectiveStock: cond === "DEFECTIVE" ? qty : 0,
						CinBorrowedStock: 0,
						CinLostStock: cond === "LOST" ? qty : 0,
						SecStatus: 1,
						UsrCreateID: userID,
						UsrUpdateID: userID,
					};
					const saveRes = await _sContainerInventoryService.save(newTargetPayload, userID);
					if (saveRes && saveRes.data && saveRes.data.CinID > 0) {
						targetCinID = saveRes.data.CinID;
					}
				}

				// 4. Registrar en el Kardex de auditoría (Detalle)
				// NOTA: Se usa CidMovementType 5 (Salida por Traslado) y 6 (Ingreso por Traslado).
				// Al no ser movimientos de ajuste directo (1,2,3,4), el SP LgsContainerInventoryDetail_I
				// NO altera CinCurrentStock, eliminando de raíz el doble consumo.
				try {
					const originLocName = this.selectedItem.CinLocationName || "Origen";
					const detailDoc = (this.transferModel.Document || "").trim();
					const userObs = (this.transferModel.Observation || "").trim();

					const originDetail = {
						CinID: this.selectedItem.CinID,
						CidMovementType: 5, // Traslado a otra Planta (Salida)
						CidQuantity: qty,
						CidDocument: detailDoc,
						CidObservation: `Traslado hacia otra planta [${condLabel}]: ${userObs || "Reubicación de envases"}`,
					};
					await _sContainerInventoryService.saveDetail(originDetail, userID);

					if (targetCinID && targetCinID > 0) {
						const targetDetail = {
							CinID: targetCinID,
							CidMovementType: 6, // Traslado desde otra Planta (Ingreso)
							CidQuantity: qty,
							CidDocument: detailDoc,
							CidObservation: `Recepción por traslado desde ${originLocName} [${condLabel}]: ${userObs || "Reubicación de envases"}`,
						};
						await _sContainerInventoryService.saveDetail(targetDetail, userID);
					}
				} catch (errDet) {
					console.warn("No se pudo registrar kardex detail (no crítico):", errDet);
				}

				this.$fun.alert("Traslado entre plantas registrado con éxito", "success");
				this.transferDialog = false;

				if (this.$refs.gridContainerInventory && typeof this.$refs.gridContainerInventory.refresh === "function") {
					this.$refs.gridContainerInventory.refresh();
				}
			} catch (error) {
				console.error("Error al registrar traslado:", error);
				const msg =
					error.response?.data?.Message ||
					error.response?.data ||
					error.message ||
					"Error al procesar el traslado entre plantas";
				this.$fun.alert(msg, "error");
			} finally {
				this.loadingTransfer = false;
			}
		},
		openHistoryModal(item) {
			this.selectedItem = Object.assign({}, item);
			this.historyDialog = true;
			this.loadHistory(item.CinID);
		},
		loadHistory(cinID) {
			this.loadingHistory = true;
			this.historyList = [];
			_sContainerInventoryService
				.paginationDetail({ start: 0, length: 100, draw: 1, searchText: "" }, cinID, this.$fun.getUserID())
				.then((response) => {
					if (response.status === 200) {
						this.historyList = response.data?.data || response.data || [];
					}
				})
				.catch((error) => {
					console.error("Error al cargar historial:", error);
					this.$fun.alert("Ocurrió un error al cargar el historial de transacciones", "error");
				})
				.finally(() => {
					this.loadingHistory = false;
				});
		},
		confirmAdjust() {
			if (!this.adjustModel.CidMovementType) {
				this.$fun.alert("Por favor seleccione el Tipo de Movimiento", "warning");
				return;
			}
			const qty = parseFloat(this.adjustModel.CidQuantity) || 0;
			if (qty <= 0) {
				this.$fun.alert("La cantidad a registrar debe ser mayor a 0", "warning");
				return;
			}
			if (!this.adjustModel.CidObservation || !this.adjustModel.CidObservation.trim()) {
				this.$fun.alert("Por favor ingrese el motivo u observación del ajuste", "warning");
				return;
			}

			this.$fun
				.alert("¿Está seguro de registrar este ajuste de stock?", "question")
				.then((result) => {
					if (result && (result.value || result.isConfirmed)) {
						const payload = {
							CinID: this.selectedItem.CinID,
							CidMovementType: parseInt(this.adjustModel.CidMovementType),
							CidQuantity: qty,
							CidDocument: (this.adjustModel.CidDocument || "").trim(),
							CidObservation: (this.adjustModel.CidObservation || "").trim(),
						};

						_sContainerInventoryService
							.saveDetail(payload, this.$fun.getUserID())
							.then((response) => {
								if (response.status === 200) {
									this.$fun.alert("Ajuste de stock registrado exitosamente", "success");
									this.adjustDialog = false;
									if (this.$refs.gridContainerInventory) {
										if (typeof this.$refs.gridContainerInventory.refresh === "function") {
											this.$refs.gridContainerInventory.refresh();
										}
									}
								}
							})
							.catch((error) => {
								console.error("Error al registrar ajuste:", error);
								const msg =
									error.response?.data?.Message ||
									error.response?.data ||
									error.message ||
									"Error al registrar el ajuste de stock";
								this.$fun.alert(msg, "error");
							});
					}
				});
		},
		formatDateTime(val) {
			if (!val) return "";
			return moment(val).format("DD/MM/YYYY HH:mm");
		},
		getMovementColor(item) {
			const helper = (item?.CidMovementHelper || "").toUpperCase();
			const val = item?.CidMovementType;
			if (helper === "IN" || val == 1 || val == 2) {
				return val == 1 ? "green darken-1" : "blue darken-1";
			} else if (helper === "OUT" || val == 3) {
				return "orange darken-2";
			} else if (helper === "LOST" || val == 4) {
				return "red darken-1";
			} else if (helper === "TRANSFER_OUT" || val == 5) {
				return "indigo darken-2";
			} else if (helper === "TRANSFER_IN" || val == 6) {
				return "teal darken-2";
			}
			return "blue-grey";
		},
		getQuantityClass(item) {
			const helper = (item?.CidMovementHelper || "").toUpperCase();
			const val = item?.CidMovementType;
			if (helper === "IN" || helper === "TRANSFER_IN" || val == 1 || val == 2 || val == 6) {
				return "green--text text--darken-2";
			}
			return "red--text text--darken-2";
		},
		formatQuantity(item) {
			const helper = (item?.CidMovementHelper || "").toUpperCase();
			const val = item?.CidMovementType;
			const isPlus = helper === "IN" || helper === "TRANSFER_IN" || item?.CidMovementAbbr === "+" || val == 1 || val == 2 || val == 6;
			const sign = isPlus ? "+" : "-";
			return `${sign} ${item?.CidQuantity || 0}`;
		},
		hasHistory(item) {
			if (!item) return false;
			return Boolean(item.CinDetailCount && item.CinDetailCount > 0);
		},
		async exportToExcel() {
			this.exportingExcel = true;
			try {
				const exportHeaders = (this.config.headers || []).filter((h) => h.value !== "actions");
				let itemsToExport = [];

				const res = await _sContainerInventoryService.pagination(
					{
						draw: 1,
						start: 0,
						length: 5000,
						filter: this.filter,
						order: [{ column: "", dir: "asc" }],
						searchText: "",
					},
					this.$fun.getUserID()
				);

				if (res && res.data && res.data.data && res.data.data.length > 0) {
					itemsToExport = res.data.data;
				} else if (this.$refs.gridContainerInventory && this.$refs.gridContainerInventory.desserts) {
					itemsToExport = this.$refs.gridContainerInventory.desserts;
				}

				if (!itemsToExport || itemsToExport.length === 0) {
					this.$fun.alert("No hay registros disponibles para exportar.", "warning");
					return;
				}

				await exportVuetifyToExcel(exportHeaders, itemsToExport, {
					title: "SUNSHINE EXPORT - INVENTARIO DE ENVASES",
					subtitle: `Generado el: ${moment().format("DD/MM/YYYY HH:mm")}`,
					filename: `Inventario_Envases_${moment().format("YYYYMMDD_HHmm")}`,
					color: "0F766E",
				});
			} catch (err) {
				console.error("Error al exportar inventario a Excel:", err);
				this.$fun.alert("Ocurrió un error al exportar a Excel.", "error");
			} finally {
				this.exportingExcel = false;
			}
		},
	},
};
</script>

<style scoped>
.cm-form-card {
	border-radius: 12px;
	padding: 16px;
	border: 1px solid rgba(0, 0, 0, 0.08);
}
.cm-tone-teal {
	background: #f0fdfa;
	border-color: #ccfbf1;
}
.cm-section-header {
	display: flex;
	align-items: center;
	margin-bottom: 16px;
}
.cm-section-icon {
	width: 32px;
	height: 32px;
	border-radius: 8px;
	background: #ccfbf1;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 12px;
}
.cm-section-title {
	font-weight: 700;
	font-size: 0.95rem;
	color: #0f766e;
}
.cm-section-subtitle {
	font-size: 0.75rem;
	color: #115e59;
}
.cm-modal-header {
	border-bottom: 1px solid #e2e8f0;
}
.cm-summary-card {
	border-radius: 8px;
	background: #f8fafc;
	border: 1px solid #e2e8f0;
}
.cm-projection-box {
	background: #f0fdfa;
	border: 1px dashed #0d9488;
}
.cm-history-table {
	border: 1px solid #e2e8f0;
	border-radius: 6px;
}
</style>
